import { storeLocalDaytimePreview } from "../app/LocalDaytimePreview";

export interface ILocalAuthUser {
  id: string;
  name: string;
  email: string;
  objectId: string;
}

const localAuthStorageKey = "soma.localAuth.userId";
const fallbackLocalAuthHeaderName = "x-soma-local-user";

const fallbackLocalAuthUsers: ILocalAuthUser[] = [
  {
    id: "admin",
    name: "Soma Local Admin",
    email: "admin.local@solargik.local",
    objectId: "00000000-0000-0000-0000-000000000001",
  },
  {
    id: "operator",
    name: "Soma Local Operator",
    email: "operator.local@solargik.local",
    objectId: "00000000-0000-0000-0000-000000000002",
  },
  {
    id: "viewer",
    name: "Soma Local Viewer",
    email: "viewer.local@solargik.local",
    objectId: "00000000-0000-0000-0000-000000000003",
  },
  {
    id: "engineer",
    name: "Soma Local Engineer",
    email: "engineer.local@solargik.local",
    objectId: "00000000-0000-0000-0000-000000000004",
  },
];

function parseLocalAuthUsers(): ILocalAuthUser[] {
  const configuredUsers = import.meta.env.VITE_SOMA_LOCAL_AUTH_USERS;
  if (!configuredUsers) {
    return fallbackLocalAuthUsers;
  }

  try {
    const parsedUsers = JSON.parse(configuredUsers) as Partial<ILocalAuthUser>[];
    const users = parsedUsers
      .filter((user) => user.id && user.name && user.email && user.objectId)
      .map((user) => ({
        id: user.id!,
        name: user.name!,
        email: user.email!,
        objectId: user.objectId!,
      }));

    if (users.length > 0) {
      return users;
    }
  } catch (error) {
    console.error("Failed to parse VITE_SOMA_LOCAL_AUTH_USERS.", error);
  }

  return fallbackLocalAuthUsers;
}

export const isLocalAuthEnabled = import.meta.env.VITE_SOMA_LOCAL_AUTH === "true";
export const localAuthUsers = parseLocalAuthUsers();
export const localAuthHeaderName =
  import.meta.env.VITE_SOMA_LOCAL_AUTH_HEADER_NAME || fallbackLocalAuthHeaderName;
export const localAuthDefaultUserId =
  import.meta.env.VITE_SOMA_LOCAL_AUTH_DEFAULT_USER || fallbackLocalAuthUsers[0].id;

export function getStoredLocalAuthUserId(): string | null {
  if (!isLocalAuthEnabled) {
    return null;
  }

  return sessionStorage.getItem(localAuthStorageKey);
}

export function storeLocalAuthUserId(userId: string | null) {
  if (!isLocalAuthEnabled) {
    return;
  }

  if (!userId) {
    sessionStorage.removeItem(localAuthStorageKey);
    storeLocalDaytimePreview(false);
    return;
  }

  sessionStorage.setItem(localAuthStorageKey, userId);
}

export function getLocalAuthUserById(userId: string | null | undefined): ILocalAuthUser | null {
  if (!userId) {
    return null;
  }

  return localAuthUsers.find((user) => user.id === userId) ?? null;
}
